import { useEffect, useMemo, useState } from 'react';
import { Reservation, ReservationPaxGroup, Service, ServiceBooking } from '@turizi/shared';
import { queryKeys, useAssignPaxGroupToServiceBooking, useReservationPaxGroups, useSaveServiceBooking, useServiceCategories, useServices } from '@/hooks/api-hooks';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Loader, Button, Switch, Badge, Checkbox } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IconAlertCircle, IconArrowLeft, IconCalendar, IconCheck, IconUsers } from '@tabler/icons-react';
import CardModal from '../card-modal';
import { ServiceTitle, ServiceTitleStatic } from '../services/service-title';
import GroupSizePopover from '../paxs/group-size-popover';
import { generateBookingPayload } from '@/lib/bookings-helper';
import { formatDayShort } from '@/lib/format-utils';
import { localTenant } from '@/services/session-service';
import { TagIcon } from '@/lib/tag-utils';
import ServicesCatalogBrowser from '@/components/services-catalog-browser';
import InputPaxGroup from '../paxs/input-pax-group';
import { Link } from 'react-router-dom';
import { RESERVATIONS_PATH } from '@/lib/navigation';
import { ShowPaxGroup } from '../snippets';
import ServiceCardPrivate from '../service-card-private';

interface SplitBookingGroup {
    adults: number;
    children: number;
    seniors: number;
    guests: number;
    infants: number;
}

interface ConsecutiveBookingStatus {
    index: number;
    group: SplitBookingGroup;
    service: Service;
    status: 'pending' | 'processing' | 'success' | 'error';
    error?: string;
}

const getCapacityLimit = (service: Service): number | null => {
    const limits: number[] = [];
    if (service.max_pax_per_booking) {
        limits.push(service.max_pax_per_booking);
    }
    const capacityAttr = service.self_attrs?.find(attr => attr.key === 'capacity');
    if (capacityAttr && capacityAttr.value !== undefined && capacityAttr.value !== null) {
        const val = Number(capacityAttr.value);
        if (!isNaN(val) && val > 0) {
            limits.push(val);
        }
    }
    if (limits.length === 0) return null;
    return Math.min(...limits);
};

const splitPax = (
    counts: { adults: number; children: number; seniors: number; infants: number; guests: number },
    limit: number
): SplitBookingGroup[] => {
    const totalPax = counts.adults + counts.children + counts.seniors + counts.guests;
    const numBookings = Math.ceil(totalPax / limit);

    const groups = Array.from({ length: numBookings }, () => ({
        adults: 0,
        children: 0,
        seniors: 0,
        guests: 0,
        infants: 0,
    }));

    let groupIdx = 0;

    // Distribute adults
    for (let i = 0; i < counts.adults; i++) {
        groups[groupIdx].adults++;
        groupIdx = (groupIdx + 1) % numBookings;
    }
    // Distribute seniors
    for (let i = 0; i < counts.seniors; i++) {
        groups[groupIdx].seniors++;
        groupIdx = (groupIdx + 1) % numBookings;
    }
    // Distribute guests
    for (let i = 0; i < counts.guests; i++) {
        groups[groupIdx].guests++;
        groupIdx = (groupIdx + 1) % numBookings;
    }
    // Distribute children
    for (let i = 0; i < counts.children; i++) {
        groups[groupIdx].children++;
        groupIdx = (groupIdx + 1) % numBookings;
    }

    // Distribute infants to groups with adults/seniors if possible
    let infantIdx = 0;
    for (let i = 0; i < counts.infants; i++) {
        let assigned = false;
        for (let j = 0; j < numBookings; j++) {
            const targetIdx = (infantIdx + j) % numBookings;
            if (groups[targetIdx].adults > 0 || groups[targetIdx].seniors > 0) {
                groups[targetIdx].infants++;
                infantIdx = (targetIdx + 1) % numBookings;
                assigned = true;
                break;
            }
        }
        if (!assigned) {
            groups[infantIdx % numBookings].infants++;
            infantIdx++;
        }
    }

    return groups;
};

interface Props {
    opened: boolean;
    onClose: () => void;
    onCreate?: (booking: ServiceBooking) => void;
    reservation?: Reservation | null;
}

export const BOOKING_STEPS = {
    GROUP_SIZE: 'group_size',
    CATEGORY: 'category',
    SERVICES: 'services',
    CONFIRMATION: 'confirmation',
    PROCESSING: 'processing',
} as const;

export type BookingStep = typeof BOOKING_STEPS[keyof typeof BOOKING_STEPS];


export default function SelectServiceCreateBooking({
    opened,
    onClose,
    onCreate,
    reservation,
}: Props) {
    const queryClient = useQueryClient();
    const tenant = localTenant();
    const tenant_id = reservation?.tenant_id || tenant?.id;
    const { t } = useTranslation();
    const [error, setError] = useState('');
    const [selectedServices, setSelectedServices] = useState<Service[]>([]);
    const [bookLinkedServices, setBookLinkedServices] = useState<Record<string, boolean>>({});

    const initialStartDay = reservation?.start_day || null;
    const initialEndDay = reservation?.end_day || null;

    const { data: fetchedPaxGroups = [] } = useReservationPaxGroups(reservation?.id || 0);
    const paxGroups: ReservationPaxGroup[] = useMemo(() => {
        if (fetchedPaxGroups && fetchedPaxGroups.length > 0) return fetchedPaxGroups;
        return reservation?.pax_groups || [];
    }, [fetchedPaxGroups, reservation?.pax_groups]);

    const assignGroupMutation = useAssignPaxGroupToServiceBooking();
    const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);

    const defaultCounts = useMemo(() => {
        const bookings = reservation?.service_bookings || [];
        const last = [...bookings].sort((a, b) => a.id - b.id).pop();
        return {
            adults: last?.adults || 0,
            children: last?.children || 0,
            seniors: last?.seniors || 0,
            infants: last?.infants || 0,
            guests: last?.guests || 0
        };
    }, [reservation]);

    const [adults, setAdults] = useState(defaultCounts.adults);
    const [children, setChildren] = useState(defaultCounts.children);
    const [seniors, setSeniors] = useState(defaultCounts.seniors);
    const [guests, setGuests] = useState(defaultCounts.guests);
    const [infants, setInfants] = useState(defaultCounts.infants);
    const [bookingCreated, setBookingCreated] = useState(false);
    const [step, setStep] = useState<BookingStep>(BOOKING_STEPS.GROUP_SIZE);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [dates, setDates] = useState<[string | null, string | null]>([initialStartDay, initialEndDay]);

    const [consecutiveStatuses, setConsecutiveStatuses] = useState<ConsecutiveBookingStatus[]>([]);
    const [currentBookingIndex, setCurrentBookingIndex] = useState<number>(-1);

    const handleToggleGroup = (group: ReservationPaxGroup) => {
        if (!group.id) return;
        const isCurrentlySelected = selectedGroupIds.includes(group.id);
        const nextIds = isCurrentlySelected
            ? selectedGroupIds.filter(id => id !== group.id)
            : [...selectedGroupIds, group.id];

        const selectedGroups = paxGroups.filter(g => g.id && nextIds.includes(g.id));
        const totals = selectedGroups.reduce((acc, g) => ({
            adults: acc.adults + (g.adults || 0),
            children: acc.children + (g.children || 0),
            seniors: acc.seniors + (g.seniors || 0),
            infants: acc.infants + (g.infants || 0),
            guests: acc.guests + (g.guests || 0),
        }), { adults: 0, children: 0, seniors: 0, infants: 0, guests: 0 });

        setSelectedGroupIds(nextIds);
        setAdults(totals.adults);
        setChildren(totals.children);
        setSeniors(totals.seniors);
        setInfants(totals.infants);
        setGuests(totals.guests);
    };

    const {
        data: categoriesData,
        isLoading: isLoadingCategories
    } = useServiceCategories(
        { tenant_id },
        { enabled: opened }
    );

    const {
        data: servicesData
    } = useServices(
        {
            tenant_id,
            category: selectedCategory,
        },
        {
            enabled: opened && !!selectedCategory,
            staleTime: 0,
            refetchOnMount: 'always'
        }
    );
    const rawServices = useMemo(() => servicesData?.data || [], [servicesData?.data]);

    const categories = useMemo(() =>
        (categoriesData?.data || [])
            .filter(Boolean)
            .sort((a, b) => {
                const rankA = a.rank ?? Infinity;
                const rankB = b.rank ?? Infinity;
                return rankA - rankB;
            })
        , [categoriesData?.data]);


    useEffect(() => {
        if (opened) {
            queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
            setStep(BOOKING_STEPS.GROUP_SIZE);
            setSelectedCategory(null);
            setDates([initialStartDay, initialEndDay]);
            setAdults(defaultCounts.adults);
            setChildren(defaultCounts.children);
            setSeniors(defaultCounts.seniors);
            setInfants(defaultCounts.infants);
            setGuests(defaultCounts.guests);
            setSelectedServices([]);
            setSelectedGroupIds([]);
            setBookLinkedServices({});
            setError('');
            setBookingCreated(false);
            setConsecutiveStatuses([]);
            setCurrentBookingIndex(-1);
        }
    }, [opened]);

    useEffect(() => {
        if (!opened || paxGroups.length === 0) return;
        if (paxGroups.length === 1 && adults + children + seniors + infants + guests === 0) {
            const single = paxGroups[0];
            if (single.id) {
                setSelectedGroupIds([single.id]);
            }
            setAdults(single.adults || 0);
            setChildren(single.children || 0);
            setSeniors(single.seniors || 0);
            setInfants(single.infants || 0);
            setGuests(single.guests || 0);
        }
    }, [opened, paxGroups]);

    const handleSelectCategory = (cat: string) => {
        queryClient.invalidateQueries({ queryKey: queryKeys.services.all });
        setSelectedCategory(cat);
        setStep(BOOKING_STEPS.SERVICES);
    };

    const createBookingMutation = useSaveServiceBooking();

    const handleToggleService = (service: Service) => {
        setSelectedServices(prev => {
            const exists = prev.some(s => s.id === service.id);
            if (exists) {
                return prev.filter(s => s.id !== service.id);
            } else {
                return [...prev, service];
            }
        });
    };

    const getLinkedServices = (svc: Service): Service[] => {
        if (svc.linked_services && svc.linked_services.length > 0) {
            return svc.linked_services;
        }
        const legacy: Service[] = [];
        if (svc.linked_service) {
            legacy.push(svc.linked_service);
        } else if (svc.linked_service_id) {
            const found = rawServices.find(s => Number(s.id) === Number(svc.linked_service_id));
            if (found) legacy.push(found);
        }
        return legacy;
    };

    const handleInitConfirmation = () => {
        if (selectedServices.length === 0) return;
        setError('');
        setBookingCreated(false);

        const initialLinkedMap: Record<string, boolean> = {};
        selectedServices.forEach(s => {
            const linkedList = getLinkedServices(s);
            linkedList.forEach(linked => {
                initialLinkedMap[`${s.id}-${linked.id}`] = false;
            });
        });
        setBookLinkedServices(initialLinkedMap);
        setConsecutiveStatuses([]);
        setCurrentBookingIndex(-1);
        setStep(BOOKING_STEPS.CONFIRMATION);
    };

    const startSequentialBookings = async () => {
        if (currentBookingIndex >= 0 || selectedServices.length === 0) return;

        setError('');
        setBookingCreated(false);

        let statuses: ConsecutiveBookingStatus[] = [];
        let taskIndex = 0;

        for (const service of selectedServices) {
            const limit = getCapacityLimit(service);
            const totalPax = adults + children + seniors + guests;
            const groups = (limit !== null && totalPax > limit)
                ? splitPax({ adults, children, seniors, guests, infants }, limit)
                : [{ adults, children, seniors, guests, infants }];

            const linkedList = getLinkedServices(service);

            for (let i = 0; i < groups.length; i++) {
                statuses.push({
                    index: taskIndex,
                    group: groups[i],
                    service: service,
                    status: taskIndex === 0 ? 'processing' as const : 'pending' as const
                });
                taskIndex++;

                for (const linkedSvc of linkedList) {
                    const isLinkedEnabled = Boolean(bookLinkedServices[`${service.id}-${linkedSvc.id}`]);
                    if (isLinkedEnabled) {
                        statuses.push({
                            index: taskIndex,
                            group: groups[i],
                            service: linkedSvc,
                            status: 'pending' as const
                        });
                        taskIndex++;
                    }
                }
            }
        }

        setConsecutiveStatuses(statuses);
        setStep(BOOKING_STEPS.PROCESSING);

        for (let i = 0; i < statuses.length; i++) {
            setCurrentBookingIndex(i);

            statuses = statuses.map((s, idx) => {
                if (idx === i) return { ...s, status: 'processing' as const };
                return s;
            });
            setConsecutiveStatuses(statuses);

            const task = statuses[i];
            const payload = {
                ...generateBookingPayload({
                    service: task.service,
                    adults: task.group.adults,
                    children: task.group.children,
                    seniors: task.group.seniors,
                    infants: task.group.infants,
                    guests: task.group.guests,
                    dates,
                    discount: null,
                    reservation,
                }),
                bypass_dedup: true,
                booking_index: i,
                timestamp: Date.now()
            };

            try {
                const result = await createBookingMutation.mutateAsync(payload);
                const booking = result?.data;

                if (booking?.id && reservation?.id && selectedGroupIds.length > 0) {
                    for (const groupId of selectedGroupIds) {
                        await assignGroupMutation.mutateAsync({
                            serviceBookingId: booking.id,
                            groupId,
                            reservationId: reservation.id
                        }).catch(() => { });
                    }
                }

                statuses = statuses.map((s, idx) => {
                    if (idx === i) return { ...s, status: 'success' as const };
                    if (idx === i + 1) return { ...s, status: 'processing' as const };
                    return s;
                });
                setConsecutiveStatuses(statuses);
                onCreate?.(booking);
            } catch (errorVal: any) {
                statuses = statuses.map((s, idx) => {
                    if (idx === i) return { ...s, status: 'error' as const, error: errorVal.message || 'Error' };
                    return s;
                });
                setConsecutiveStatuses(statuses);
                setError(t(errorVal.message) || errorVal.message || 'Error creating booking');
                return;
            }
        }

        setBookingCreated(true);
        setTimeout(() => {
            handleClose();
        }, 2000);
    };

    const handleClose = () => {
        onClose();
    };

    // without a reservation
    if (!reservation) return null;

    return (
        <CardModal
            opened={opened}
            onClose={handleClose}
            size="fit"
            header={
                < div className="flex justify-center gap-4" >
                    {
                        reservation ? (
                            <Link
                                to={`${RESERVATIONS_PATH}/${reservation.id}`}
                                className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-[11px] font-semibold border border-blue-100 hover:bg-blue-100 transition-colors"
                            >
                                <IconCalendar size={12} />
                                <span>{t('reservation')} #{reservation.id}</span>
                                <span className="opacity-50 mx-0.5">•</span>
                                <span>{formatDayShort(reservation.start_day)} - {formatDayShort(reservation.end_day)}</span>
                            </Link >
                        ) : null
                    }
                    <div className="flex gap-2">
                        <GroupSizePopover
                            adults={adults}
                            children={children}
                            seniors={seniors}
                            infants={infants}
                            guests={guests}
                            onAccept={(counts) => {
                                setAdults(counts.adults);
                                setChildren(counts.children);
                                setSeniors(counts.seniors);
                                setInfants(counts.infants);
                                setGuests(counts.guests || 0);
                            }}
                        />
                    </div>
                </div >
            }
        >
            {step === BOOKING_STEPS.GROUP_SIZE && (
                <div className="w-fit mx-auto">
                    <div className="text-center mb-4">
                        <h3 className="text-base font-semibold text-gray-900">{t('enter_group_size')}</h3>
                    </div>

                    {paxGroups.length > 0 && (
                        <div className="space-y-2.5">
                            <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-xs">
                                <table className="w-full text-left border-collapse">
                                    <tbody className="divide-y divide-gray-100 text-xs">
                                        {paxGroups.map((group) => {
                                            const isSelected = !!group.id && selectedGroupIds.includes(group.id);
                                            const totalHeadcount = group.pax_count || ((group.adults || 0) + (group.children || 0) + (group.seniors || 0) + (group.guests || 0));

                                            return (
                                                <tr
                                                    key={group.id}
                                                    onClick={() => handleToggleGroup(group)}
                                                    className={`transition-colors cursor-pointer select-none ${isSelected
                                                        ? 'bg-indigo-50/70 hover:bg-indigo-100/50'
                                                        : 'bg-white hover:bg-gray-50/80'
                                                        }`}
                                                >
                                                    <td className="w-10 px-3 py-2.5 text-center align-middle">
                                                        <Checkbox
                                                            color="indigo"
                                                            radius="sm"
                                                            size="xs"
                                                            checked={isSelected}
                                                            onChange={() => { }}
                                                            className="pointer-events-none"
                                                        />
                                                    </td>
                                                    <td className="px-3 py-2.5 align-middle">
                                                        <div className="flex items-center gap-2.5">
                                                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 transition-colors ${isSelected ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600'
                                                                }`}>
                                                                <IconUsers size={14} />
                                                            </div>
                                                            <div className="min-w-0">
                                                                <div className="font-semibold text-xs text-gray-900 truncate leading-tight" title={group.name}>
                                                                    {group.name}
                                                                </div>
                                                                {group.persons && group.persons.length > 0 && (
                                                                    <div className="text-[11px] text-gray-500 truncate max-w-xs mt-0.5 leading-tight" title={group.persons.map(p => p.name).filter(Boolean).join(', ')}>
                                                                        {group.persons.map(p => p.name).filter(Boolean).join(', ')}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-2.5 text-right align-middle">
                                                        <div className="inline-flex justify-end">
                                                            <ShowPaxGroup
                                                                adults={group.adults}
                                                                children={group.children}
                                                                seniors={group.seniors}
                                                                infants={group.infants}
                                                                guests={group.guests}
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <div className="relative my-4">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-gray-200" />
                                </div>
                                <div className="relative flex justify-center text-xs uppercase">
                                    <span className="bg-white px-2 text-gray-400 font-medium">
                                        {t('customize_group_size')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    <InputPaxGroup
                        adults={adults}
                        children={children}
                        seniors={seniors}
                        infants={infants}
                        guests={guests}
                        onChange={(counts) => {
                            setAdults(counts.adults);
                            setChildren(counts.children);
                            setSeniors(counts.seniors);
                            setInfants(counts.infants);
                            setGuests(counts.guests || 0);

                            if (selectedGroupIds.length > 0) {
                                const selectedGroups = paxGroups.filter(g => g.id && selectedGroupIds.includes(g.id));
                                const sumSelected = selectedGroups.reduce((acc, g) => ({
                                    adults: acc.adults + (g.adults || 0),
                                    children: acc.children + (g.children || 0),
                                    seniors: acc.seniors + (g.seniors || 0),
                                    infants: acc.infants + (g.infants || 0),
                                    guests: acc.guests + (g.guests || 0),
                                }), { adults: 0, children: 0, seniors: 0, infants: 0, guests: 0 });

                                const matches =
                                    counts.adults === sumSelected.adults &&
                                    counts.children === sumSelected.children &&
                                    counts.seniors === sumSelected.seniors &&
                                    counts.infants === sumSelected.infants &&
                                    (counts.guests || 0) === sumSelected.guests;

                                if (!matches) {
                                    setSelectedGroupIds([]);
                                }
                            }
                        }}
                    />
                    <div className="flex justify-end pt-2">
                        <Button
                            color="black"
                            radius="md"
                            fullWidth
                            onClick={() => setStep(BOOKING_STEPS.CATEGORY)}
                            disabled={adults + children + seniors + infants + guests === 0}
                        >
                            {t('continue')}
                        </Button>
                    </div>
                </div>
            )}

            {
                step === BOOKING_STEPS.CATEGORY && (
                    <div className="space-y-4">
                        {isLoadingCategories ? (
                            <div className="flex justify-center py-8">
                                <Loader color="black" />
                            </div>
                        ) : (
                            <div className="grid grid-cols-4 gap-2">
                                {categories.map((catObj) => {
                                    const cat = catObj.category;
                                    return (
                                        <div
                                            key={cat}
                                            onClick={() => handleSelectCategory(cat)}
                                            className="solid-box-border-hover flex flex-col items-center"
                                        >
                                            <div className="p-4">
                                                <TagIcon name={cat} size="md" hideText />
                                            </div>
                                            <div className="font-semibold text-sm text-gray-800 text-center">
                                                {t(cat)}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )
            }

            {
                step === BOOKING_STEPS.SERVICES && selectedCategory && (
                    <div className="space-y-4 pb-12">
                        {/* Breadcrumbs / Back button */}
                        <div className="flex items-center justify-between pb-2 border-b border-gray-100 text-xs text-gray-500">
                            <div className="flex items-center gap-2">
                                <span
                                    className="hover:text-blue-600 cursor-pointer font-medium"
                                    onClick={() => {
                                        setSelectedCategory(null);
                                        setStep(BOOKING_STEPS.CATEGORY);
                                    }}
                                >
                                    {t('services')}
                                </span>
                                <span>&gt;</span>
                                <span className="font-bold text-gray-800 flex items-center gap-1 capitalize">
                                    {t(selectedCategory)}
                                </span>
                            </div>
                            {selectedServices.length > 0 && (
                                <Badge color="blue" variant="light" size="sm">
                                    {t('selected_services_count', { count: selectedServices.length })}
                                </Badge>
                            )}
                        </div>
                        <ServicesCatalogBrowser
                            selectedCategory={selectedCategory}
                            dateRange={dates}
                            onDateRangeChange={setDates}
                            enableBulkActions={true}
                            selectedServices={selectedServices.map(s => String(s.id))}
                            toggleServiceSelection={(id) => {
                                const serviceObj = rawServices.find(s => String(s.id) === String(id));
                                if (serviceObj) handleToggleService(serviceObj);
                            }}
                            onSelectService={(service) => {
                                handleToggleService(service);
                            }}
                            paxCount={adults + children + seniors + guests}
                            tenantId={reservation?.tenant_id}
                            reservationId={reservation?.id}
                        />

                        {/* Sticky Footer Bar for Multi-Service Selection */}
                        <div className="sticky bottom-0 bg-white/95 backdrop-blur-xs border-t border-gray-200 p-3 mt-4 -mx-6 -mb-6 flex items-center justify-between gap-4 z-20 shadow-lg rounded-b-xl">
                            <div className="flex items-center gap-2 text-xs font-semibold text-gray-700">
                                <Badge color={selectedServices.length > 0 ? 'blue' : 'gray'} variant="light" size="lg">
                                    {t('selected_services_count', { count: selectedServices.length })}
                                </Badge>
                                {selectedServices.length > 0 && (
                                    <Button
                                        variant="subtle"
                                        color="gray"
                                        size="xs"
                                        onClick={() => setSelectedServices([])}
                                    >
                                        {t('deselect_all')}
                                    </Button>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="subtle"
                                    color="gray"
                                    size="sm"
                                    onClick={() => {
                                        setSelectedCategory(null);
                                        setStep(BOOKING_STEPS.CATEGORY);
                                    }}
                                >
                                    {t('back')}
                                </Button>
                                <Button
                                    color="black"
                                    size="sm"
                                    radius="md"
                                    disabled={selectedServices.length === 0}
                                    onClick={handleInitConfirmation}
                                >
                                    {t('continue')} {selectedServices.length > 0 ? `(${selectedServices.length})` : ''}
                                </Button>
                            </div>
                        </div>
                    </div>
                )
            }

            {
                step === BOOKING_STEPS.CONFIRMATION && selectedServices.length > 0 && (
                    <div className="space-y-4">
                        <div className="max-h-[60vh] overflow-y-auto space-y-3 pr-1">
                            {selectedServices.map((svc) => {
                                const limit = getCapacityLimit(svc);
                                const totalPax = adults + children + seniors + guests;
                                const serviceSplitNeeded = limit !== null && totalPax > limit;
                                const serviceBookingGroups = serviceSplitNeeded
                                    ? splitPax({ adults, children, seniors, guests, infants }, limit)
                                    : [{ adults, children, seniors, guests, infants }];

                                const linkedList = getLinkedServices(svc);

                                return (
                                    <div key={svc.id} className="border border-gray-200 rounded-xl p-4 bg-white space-y-3 shadow-2xs">
                                        <div className="flex items-start justify-between border-b border-gray-100 pb-2">
                                            <ServiceTitle service={svc} />
                                            <TagIcon name={svc.category} />
                                        </div>

                                        {/* Linked Services Switches for this service */}
                                        {linkedList.length > 0 && (
                                            <div className="space-y-3">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-bold text-gray-800">
                                                        {t('book_linked_services_question')}
                                                    </span>
                                                </div>

                                                <div className="space-y-2">
                                                    {linkedList.map((linkedSvc) => {
                                                        const linkKey = `${svc.id}-${linkedSvc.id}`;
                                                        const isLinkedEnabled = Boolean(bookLinkedServices[linkKey]);

                                                        return (
                                                            <div
                                                                key={linkedSvc.id}
                                                                className={`
                                                                flex gap-4
                                                                solid-box-border
                                                            `}
                                                            >
                                                                <Switch
                                                                    size="sm"
                                                                    color="black"
                                                                    checked={isLinkedEnabled}
                                                                    onChange={(event) => {
                                                                        const checked = event.currentTarget.checked;
                                                                        setBookLinkedServices(prev => ({
                                                                            ...prev,
                                                                            [linkKey]: checked
                                                                        }));
                                                                    }}
                                                                    className="shrink-0 cursor-pointer"
                                                                />
                                                                <ServiceCardPrivate
                                                                    service={linkedSvc}
                                                                    className='text-xs solid-box-border-tinted flex-1'
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {/* Capacity warning / split groups for this service */}
                                        {serviceSplitNeeded && (
                                            <div className="space-y-2">
                                                <Alert icon={<IconAlertCircle size={16} />} color="blue" variant="light" className="py-2">
                                                    <div className="text-xs font-semibold mb-0.5">
                                                        {t('group_size_exceeds_capacity')}
                                                    </div>
                                                    <div className="text-xxs">
                                                        {t('capacity_warning_text', {
                                                            capacity: limit,
                                                            total: totalPax,
                                                            count: serviceBookingGroups.length
                                                        })}
                                                    </div>
                                                </Alert>
                                                <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden bg-gray-50/50 text-xs">
                                                    {serviceBookingGroups.map((group, idx) => (
                                                        <div key={idx} className="p-2 flex justify-between items-center">
                                                            <span className="font-semibold text-gray-700 text-xxs">
                                                                {t('booking')} #{idx + 1}
                                                            </span>
                                                            <ShowPaxGroup
                                                                adults={group.adults}
                                                                children={group.children}
                                                                seniors={group.seniors}
                                                                infants={group.infants}
                                                                guests={group.guests}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Pax Group Summary */}
                        <div className="border border-gray-100 rounded-xl p-3 bg-gray-50/50 space-y-2">
                            <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                                {t('passengers')}
                            </div>
                            <ShowPaxGroup
                                adults={adults}
                                children={children}
                                seniors={seniors}
                                infants={infants}
                                guests={guests}
                            />
                        </div>

                        <div className="flex gap-3 pt-3 border-t border-gray-100">
                            <Button
                                variant="subtle"
                                color="gray"
                                radius="md"
                                fullWidth
                                leftSection={<IconArrowLeft size={16} />}
                                onClick={() => {
                                    setStep(BOOKING_STEPS.SERVICES);
                                }}
                            >
                                {t('back')}
                            </Button>
                            <Button
                                color="black"
                                radius="md"
                                fullWidth
                                onClick={startSequentialBookings}
                                disabled={bookingCreated}
                            >
                                {t('confirm')} ({selectedServices.length})
                            </Button>
                        </div>
                    </div>
                )
            }

            {
                step === BOOKING_STEPS.PROCESSING && (
                    <div className="space-y-4 py-2 max-w-xl mx-auto">
                        <div className="border border-gray-100 rounded-xl divide-y divide-gray-100 overflow-hidden shadow-sm">
                            {consecutiveStatuses.map((status, idx) => {
                                const group = status.group;
                                return (
                                    <div key={idx} className="p-3.5 text-sm flex justify-between items-center bg-white transition-all">
                                        <div className="flex flex-col min-w-0 pr-2">
                                            <span className="font-semibold text-gray-800 text-xs">
                                                {ServiceTitleStatic(status.service)}
                                            </span>
                                            <span className="text-[9px] text-gray-400 font-light">
                                                {t('booking')} #{idx + 1}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <ShowPaxGroup
                                                adults={group.adults}
                                                children={group.children}
                                                seniors={group.seniors}
                                                infants={group.infants}
                                                guests={group.guests}
                                            />
                                            <div>
                                                {status.status === 'pending' && (
                                                    <span className="text-[10px] text-gray-400 font-bold bg-gray-100 px-2.5 py-1 rounded-full uppercase tracking-wider">
                                                        {t('pending')}
                                                    </span>
                                                )}
                                                {status.status === 'processing' && (
                                                    <div className="flex items-center gap-2 text-blue-600 bg-blue-50/50 px-2.5 py-1 rounded-full border border-blue-100">
                                                        <Loader size={10} color="blue" />
                                                        <span className="text-[10px] font-bold uppercase tracking-wider animate-pulse">{t('creating')}</span>
                                                    </div>
                                                )}
                                                {status.status === 'success' && (
                                                    <div className="flex items-center gap-1 text-green-600 bg-green-50 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border border-green-100">
                                                        <IconCheck size={12} stroke={3} />
                                                        <span>{t('created')}</span>
                                                    </div>
                                                )}
                                                {status.status === 'error' && (
                                                    <div className="flex items-center gap-1 text-red-600 bg-red-50 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border border-red-100">
                                                        <IconAlertCircle size={12} stroke={3} />
                                                        <span>{t('failed')}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {error && (
                            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" radius="md">
                                {error}
                            </Alert>
                        )}

                        {bookingCreated && (
                            <Alert icon={<IconCheck size={16} />} color="green" variant="light" radius="md">
                                {consecutiveStatuses.length > 1
                                    ? t('all_bookings_successfully_created')
                                    : t('booking_successfully_created')
                                }
                            </Alert>
                        )}

                        {error && (
                            <Button
                                color="black"
                                radius="md"
                                fullWidth
                                onClick={handleClose}
                            >
                                {t('close')}
                            </Button>
                        )}
                    </div>
                )
            }
        </CardModal >
    );
}
